import * as React from "react";

import type { VideoDockFeatureProps } from "@/components/OmniCine/Docks/video/videoDock.types";

export default function VideoDockFeature({
  previewStatus,
  previewUrl,
  previewLabel,
  err,
  mediaIsPlaying,
  stageHeight,
  previewVideoRef,
  onGlassPlayPause,
  syncActiveMediaState,
}: VideoDockFeatureProps): JSX.Element {
  return (
    <div
      className="rounded-xl border border-cyan-200/22 bg-[linear-gradient(180deg,rgba(8,28,36,0.32)_0%,rgba(5,10,18,0.55)_100%)] overflow-hidden flex items-center justify-center shadow-[inset_0_1px_0_rgba(160,240,255,0.08)]"
      style={{ height: stageHeight }}
    >
      {previewStatus === "loading" && <div className="text-sm opacity-70">Resolving URL...</div>}

      {previewStatus === "error" && (
        <div className="px-3 text-sm text-red-200">
          <div className="font-medium">Preview error</div>
          <div className="opacity-80 mt-1">{err}</div>
        </div>
      )}

      {previewStatus === "ready" && previewUrl && (
        <div className="w-full h-full p-2">
          <div className="relative w-full h-full rounded-lg overflow-hidden border border-cyan-100/40 bg-black shadow-[inset_0_0_0_1px_rgba(205,246,255,0.2),0_0_20px_rgba(56,189,248,0.12)]">
            <div className="pointer-events-none absolute inset-x-0 top-0 z-[2] h-6 border-b border-cyan-100/20 bg-[linear-gradient(180deg,rgba(150,235,255,0.16)_0%,rgba(56,189,248,0.08)_55%,rgba(8,20,30,0.04)_100%)]" />
            <video
              ref={previewVideoRef}
              controls={false}
              preload="metadata"
              playsInline
              src={previewUrl}
              aria-label={previewLabel}
              className="w-full h-full object-cover bg-black"
              onClick={onGlassPlayPause}
              onLoadedMetadata={syncActiveMediaState}
              onDurationChange={syncActiveMediaState}
              onTimeUpdate={syncActiveMediaState}
              onPlay={syncActiveMediaState}
              onPause={syncActiveMediaState}
              onEnded={syncActiveMediaState}
              onVolumeChange={syncActiveMediaState}
            />

            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(4,12,20,0.38)_0%,rgba(4,12,20,0.08)_34%,rgba(4,12,20,0.22)_100%)]" />
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-cyan-100/55" />
            {!mediaIsPlaying && <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(0,0,0,0.1)_0%,rgba(0,0,0,0.42)_70%)]" />}

            {!mediaIsPlaying && (
              <button
                type="button"
                aria-label="Play video"
                onClick={onGlassPlayPause}
                className="absolute left-1/2 top-1/2 grid h-14 w-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-cyan-100/90 bg-cyan-300/24 text-cyan-50 backdrop-blur-0"
                style={{ boxShadow: "0 0 14px rgba(34,211,238,0.5), 0 0 26px rgba(34,211,238,0.24), inset 0 0 10px rgba(125,211,252,0.3)" }}
              >
                <span className="pointer-events-none absolute inset-1 rounded-full border border-cyan-100/35" />
                <span className="block text-xl leading-none pl-0.5">▶</span>
              </button>
            )}
          </div>
        </div>
      )}

      {previewStatus === "ready" && !previewUrl && <div className="text-sm opacity-70">No media selected</div>}
    </div>
  );
}